import { emit } from '@zapier/events-toolkit-browser';
import { WebAnalyticsTrackingUserInteractionSignupUserInteractionEvent } from '@zapier/avro-events';

import { SignupType } from '../types';
import { captureException } from '@sentry/react';

type signup_funnel_union =
  WebAnalyticsTrackingUserInteractionSignupUserInteractionEvent.signup_funnel_union;
type signup_type_union =
  WebAnalyticsTrackingUserInteractionSignupUserInteractionEvent.signup_type_union;

type Params = {
  signupFunnel: signup_funnel_union;
  signupType: signup_type_union;
  selectedApis?: string[] | string | undefined;
};

/*
 * Related AVRO schema: https://gitlab.com/zapier/avro-schemas/-/blob/main/schemas/web_analytics/tracking/user_interaction/signup_user_interaction_event.avsc
 */
export function dispatchSignupUserInteractionEvent({
  signupFunnel,
  signupType,
  selectedApis = undefined,
}: Params) {
  const signup_location =
    typeof window !== 'undefined' ? window.location.href : 'server-side';

  /*
   * Our eventing library pre-fills a bunch of fields for us, so we don't need to pass everything:
   * https://engineering.zapier.com/guides/event-streaming/creating-and-emitting-events/emitting-frontend-events/#autopopulated-fields
   *
   * We don't pass `account_id` because at this point the user doesn't have an account yet. Any account_id that might be
   * present in the browser, is from a previous session.
   */
  try {
    return emit(
      {
        system_name: 'identity',
        timestamp_ms: new Date().getTime(),
        absolute_url: window.location.toString(),
        referrer: document.referrer,
        signup_funnel: signupFunnel,
        signup_type: signupType,
        selected_apis:
          selectedApis instanceof Array ? selectedApis.join(',') : selectedApis,
        signup_location,
      },
      'web_analytics.tracking.user_interaction.SignupUserInteractionEvent'
    );
  } catch (err) {
    captureException(err, {
      tags: {
        type: 'dispatchSignupUserInteractionEvent',
      },
    });
  }
}

/*
 * Created as part of ID-905: https://zapierorg.atlassian.net/browse/ID-905
 * For now we need to support both the old events and the new Avro events. This method provides an easy solution to
 * converting the old event type to the Avro counterpart.
 */
const eventTypeMap: { [key in SignupType]: signup_type_union } = {
  'facebook-sso': 'facebook_sso',
  sso: 'sso',
  'flab-abs': 'flab_abs',
  direct: 'direct',
  'flab-sso': 'flab_sso',
  'google-one-tap': 'google_one_tap',
  'google-sso': 'google_sso',
  'microsoft-sso': 'microsoft_sso',
};

export function oldSignupTypeToAvroSignupType(
  type: SignupType
): signup_type_union {
  return eventTypeMap[type];
}
